import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, Plus, X, Monitor, ArrowLeft, CheckCircle, Image } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';

const SoftwareSubmit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company_name: '',
    category: '',
    tagline: '',
    description: '',
    features: [],
    screenshots: [],
    logo_url: '',
    website: '',
    pricing: '',
    demo_url: '',
    contact_email: ''
  });
  const [newFeature, setNewFeature] = useState('');
  const [newScreenshot, setNewScreenshot] = useState('');

  const categories = [
    { value: 'tms', label: 'Transportation Management (TMS)' },
    { value: 'wms', label: 'Warehouse Management (WMS)' },
    { value: 'quoting', label: 'Quoting & Rating' },
    { value: 'customs', label: 'Customs & Compliance' },
    { value: 'tracking', label: 'Tracking & Visibility' },
    { value: 'accounting', label: 'Accounting & Billing' },
    { value: 'other', label: 'Other' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addFeature = () => {
    if (newFeature.trim() && formData.features.length < 10) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const addScreenshot = () => {
    if (newScreenshot.trim() && formData.screenshots.length < 5) {
      setFormData(prev => ({
        ...prev,
        screenshots: [...prev.screenshots, newScreenshot.trim()]
      }));
      setNewScreenshot('');
    }
  };

  const removeScreenshot = (index) => {
    setFormData(prev => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.company_name || !formData.category || 
        !formData.tagline || !formData.description || !formData.pricing) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.features.length === 0) {
      toast.error('Please add at least one feature');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/software', formData);
      toast.success('Software submitted successfully! It will be reviewed by our team.');
      navigate('/software');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit software');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 py-8">
        <div className="max-w-3xl mx-auto px-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/software')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Marketplace
          </Button>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-6 w-6 text-cyan-500" />
                Submit Your Software
              </CardTitle>
              <CardDescription>
                List your cargo/freight software in the CTC Marketplace. All submissions are reviewed before being published.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Software Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="e.g., CargoTrack Pro"
                      data-testid="software-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Your Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      placeholder="e.g., Tech Solutions Inc."
                      data-testid="company-name"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger data-testid="category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pricing">Pricing *</Label>
                    <Input
                      id="pricing"
                      name="pricing"
                      value={formData.pricing}
                      onChange={handleChange}
                      placeholder="e.g., Starting at $99/month"
                      data-testid="pricing"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tagline">Tagline *</Label>
                  <Input
                    id="tagline"
                    name="tagline"
                    value={formData.tagline}
                    onChange={handleChange}
                    placeholder="A short, catchy description of your software"
                    maxLength={100}
                    data-testid="tagline"
                  />
                  <p className="text-xs text-slate-500">{formData.tagline.length}/100 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Full Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe what your software does, who it's for, and key benefits..."
                    rows={5}
                    data-testid="description"
                  />
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <Label>Key Features * (at least 1, max 10)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      placeholder="e.g., Real-time tracking"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                      data-testid="feature-input"
                    />
                    <Button
                      type="button"
                      onClick={addFeature}
                      disabled={formData.features.length >= 10}
                      data-testid="add-feature-btn"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.features.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.features.map((feature, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1 py-1 px-2"
                        >
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          {feature}
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="ml-1 hover:text-red-500"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Screenshots */}
                <div className="space-y-2">
                  <Label>Screenshot URLs (max 5)</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newScreenshot}
                      onChange={(e) => setNewScreenshot(e.target.value)}
                      placeholder="https://example.com/screenshot.png"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addScreenshot())}
                      data-testid="screenshot-input"
                    />
                    <Button
                      type="button"
                      onClick={addScreenshot}
                      disabled={formData.screenshots.length >= 5}
                      data-testid="add-screenshot-btn"
                    >
                      <Image className="h-4 w-4" />
                    </Button>
                  </div>
                  {formData.screenshots.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                      {formData.screenshots.map((url, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={url}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/300x200?text=Image'}
                          />
                          <button
                            type="button"
                            onClick={() => removeScreenshot(index)}
                            className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Additional Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="logo_url">Logo URL</Label>
                    <Input
                      id="logo_url"
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleChange}
                      placeholder="https://example.com/logo.png"
                      data-testid="logo-url"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="website">Website URL</Label>
                    <Input
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com"
                      data-testid="website"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="demo_url">Demo/Trial URL</Label>
                    <Input
                      id="demo_url"
                      name="demo_url"
                      value={formData.demo_url}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com/demo"
                      data-testid="demo-url"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Contact Email</Label>
                    <Input
                      id="contact_email"
                      name="contact_email"
                      type="email"
                      value={formData.contact_email}
                      onChange={handleChange}
                      placeholder="sales@yourcompany.com"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Your submission will be reviewed by our team before being published. 
                    This typically takes 1-2 business days. We'll notify you by email once approved.
                  </p>
                </div>

                <div className="flex justify-end gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/software')}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-cyan-500 hover:bg-cyan-600"
                    disabled={loading}
                    data-testid="submit-software-btn"
                  >
                    {loading ? 'Submitting...' : 'Submit for Review'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SoftwareSubmit;
